import PropTypes from 'prop-types';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { COLOR_SCHEMES, getSchemeColor, getSchemeColors, getSchemeBackground } from '../utils/color-schemas';

// Storage key for the color scheme
const STORAGE_KEY = 'ga4_report_color_scheme';

// Create context
const ColorSchemeContext = createContext();

// Hook to use the color scheme context
export const useColorScheme = () => {
  const context = useContext(ColorSchemeContext);
  // Return null instead of throwing an error if not within a provider
  // This allows components to check if the context is available and fallback gracefully
  return context;
};

// Provider component
export const ColorSchemeProvider = ({ children, initialScheme = 'default' }) => {
  // Get the saved scheme from localStorage or use the initialScheme
  const getSavedScheme = () => {
    if (typeof window !== 'undefined') {
      const savedScheme = window.localStorage.getItem(STORAGE_KEY);
      return savedScheme && COLOR_SCHEMES[savedScheme] ? savedScheme : initialScheme;
    }
    return initialScheme;
  };
  
  const [scheme, setScheme] = useState(getSavedScheme);
  
  // Save the scheme to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, scheme);
    }
  }, [scheme]);
  
  const changeScheme = useCallback((newScheme) => {
    if (COLOR_SCHEMES[newScheme]) {
      setScheme(newScheme);
      return true;
    }
    return false;
  }, []);
  
  const getColors = useCallback((count = 4, type = 'primary') => getSchemeColors(scheme, count, type), [scheme]);
  
  const getColor = useCallback((index = 0, type = 'primary') => getSchemeColor(scheme, index, type), [scheme]);
  
  const getBackground = useCallback(() => getSchemeBackground(scheme), [scheme]);
  
  const value = useMemo(() => ({
    scheme,
    schemes: Object.keys(COLOR_SCHEMES).map(key => ({
      id: key,
      name: COLOR_SCHEMES[key].name
    })),
    changeScheme,
    getColors,
    getColor,
    getBackground,
  }), [scheme, changeScheme, getColors, getColor, getBackground]);
  
  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
};

ColorSchemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialScheme: PropTypes.string,
}; 